import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Search, MapPin, ShieldCheck, Compass, FileText, Building2, Home as HomeIcon,
  Wallet, Globe2, ArrowRight, CheckCircle2, Play, ChevronLeft, ChevronRight, Plane
} from "lucide-react";
import { LineReveal, Reveal, Marquee } from "@/lib/motion";
import ListingsTicker from "@/components/ListingsTicker";

const REGIONS = [
  { name: "Central",  districts: "Kampala · Wakiso · Mukono · Luweero · Mpigi · Mityana · Kayunga" },
  { name: "Eastern",  districts: "Jinja · Iganga · Mbale · Tororo · Soroti · Busia · Kamuli" },
  { name: "Western",  districts: "Mbarara · Hoima · Masaka · Fort Portal · Kyegegwa · Kasese" },
  { name: "Northern", districts: "Gulu · Lira · Arua · Kitgum · Nebbi · Moyo · Pader · Amuru" },
];

const SERVICES = [
  { icon: ShieldCheck, t: "Land Verification", d: "Ownership review, site inspection, title checks." },
  { icon: Compass,     t: "Land Surveying",    d: "Boundary opening, mapping, subdivision." },
  { icon: FileText,    t: "Title Processing",  d: "Verification, transfers, registrations." },
  { icon: Building2,   t: "Construction",      d: "Homes, rentals, warehouses, hotels." },
  { icon: HomeIcon,    t: "Property Mgmt",     d: "Tenant placement, rent collection, maintenance." },
  { icon: Wallet,      t: "Financing",         d: "Mortgages, construction & property loans." },
  { icon: Globe2,      t: "Diaspora Services", d: "Remote buying, video inspection, monitoring." },
  { icon: Plane,       t: "Buy from Abroad",   d: "End-to-end diaspora liaison from LA to Kampala." },
];

const BANKS = ["Stanbic", "DFCU", "Centenary", "Equity", "Bank of Africa", "PostBank", "Housing Finance"];

const PACKAGES = [
  { name: "Basic", price: "UGX 50,000", points: ["Platform listing with photos", "Location & map", "Buyer inquiry inbox"], cta: "Start Basic" },
  { name: "Premium", price: "UGX 250,000", points: ["Featured placement", "WhatsApp & social promotion", "Dedicated agent follow-up"], cta: "Go Premium", featured: true },
  { name: "Corporate", price: "UGX 1M+", points: ["Estates, farms, developers", "Multi-listing campaigns", "Bespoke marketing"], cta: "Talk to Sales" },
];

const MANIFESTO = [
  { n: "01", t: "Discover",  d: "Every plot, verified. Every listing, mapped. Search Uganda with total transparency." },
  { n: "02", t: "Inspect",   d: "Book a walk-through. We film it, geotag it, and share it. Even from 8,000 miles away." },
  { n: "03", t: "Verify",    d: "Titles, boundaries, ownership. We check what no one else does — before your money moves." },
  { n: "04", t: "Close",     d: "Legal, financing, survey, transfer — all under one honest roof. From offer to keys." },
];

// Hero slideshow — 10 branded plot flyers stored in /public/uploads/hero
const HERO_SLIDES = Array.from({ length: 10 }, (_, i) => `/uploads/hero/slide-${String(i + 1).padStart(2, "0")}.png`);

const SLIDES = [
  {
    tag: "01 · CENTRAL",
    title: "Lake Victoria frontage.",
    caption: "Mukono, Katosi Road — 1 acre, ready title.",
    img: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=2000&q=85",
  },
  {
    tag: "02 · WESTERN",
    title: "1,280 acres of freehold.",
    caption: "Kyegegwa — commercial agriculture, coffee, dairy.",
    img: "https://images.unsplash.com/photo-1495107334309-fcf20504a5ab?auto=format&fit=crop&w=2000&q=85",
  },
  {
    tag: "03 · CENTRAL",
    title: "Modern gated home.",
    caption: "Naalya — 4 bedrooms, borehole, secure estate.",
    img: "https://images.unsplash.com/photo-1674758978719-6121d8fbefb4?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85",
  },
  {
    tag: "04 · NORTHERN",
    title: "Gulu Town commercial plot.",
    caption: "0.25 acre CBD — hotel, plaza, office block ready.",
    img: "https://images.unsplash.com/photo-1599619351208-3e6c839d6828?auto=format&fit=crop&w=2000&q=85",
  },
];

export default function Home() {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [meta, setMeta] = useState({ property_types: [], title_statuses: [] });
  const [search, setSearch] = useState({ q: "", property_type: "", title_status: "", budget: "" });

  useEffect(() => {
    api.get("/properties", { params: { featured: true, limit: 6 } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const onSearch = (e) => {
    e?.preventDefault?.();
    const params = new URLSearchParams();
    if (search.q) params.set("q", search.q);
    if (search.property_type) params.set("property_type", search.property_type);
    if (search.title_status) params.set("title_status", search.title_status);
    if (search.budget) params.set("budget", search.budget);
    navigate(`/listings?${params.toString()}`);
  };

  // Parallax on hero
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const yContent = useTransform(scrollYProgress, [0, 1], ["0%", "-6%"]);
  const opacityHero = useTransform(scrollYProgress, [0, 0.6], [1, 0.4]);

  return (
    <main data-testid="home-page">
      {/* CINEMATIC PHOTO SLIDESHOW HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[720px] overflow-hidden bg-secondary text-secondary-foreground" data-testid="hero">
        <motion.div className="absolute inset-0" style={{ y: yBg }} data-testid="hero-slideshow">
          <HeroSlideshow slides={HERO_SLIDES} />
          <div className="absolute inset-0 bg-gradient-to-b from-secondary/70 via-secondary/45 to-secondary/85" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(0,0,0,0.4))]" />
        </motion.div>

        <motion.div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-16 md:pb-24" style={{ y: yContent, opacity: opacityHero }}>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mb-8 flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-bold text-accent"
          >
            <span className="w-8 h-px bg-accent" /> Uganda · Since 2018 · Diaspora Ready
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="max-w-xl"
          >
            <p className="text-lg md:text-xl text-secondary-foreground/85 leading-relaxed">
              Land, homes, farms & commercial property — verified, mapped, surveyed, and closed under one roof. From Kampala to Karamoja.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => navigate("/sell")} className="rounded-full bg-primary hover:bg-primary/90 h-12 px-7 group" data-testid="hero-list-btn">
                List Your Property <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button onClick={() => navigate("/listings")} variant="outline" className="rounded-full h-12 px-7 bg-transparent border-white/30 text-white hover:bg-white/10" data-testid="hero-browse-btn">
                Browse Listings
              </Button>
              <button className="ml-2 inline-flex items-center gap-3 text-sm font-medium group" onClick={() => document.getElementById("slides")?.scrollIntoView({behavior:"smooth"})} data-testid="hero-play-btn">
                <span className="w-10 h-10 rounded-full bg-accent text-accent-foreground grid place-items-center group-hover:scale-110 transition-transform">
                  <Play className="w-4 h-4 fill-current translate-x-0.5" />
                </span>
                Watch showcase
              </button>
            </div>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-[10px] tracking-[0.3em] uppercase font-bold text-white/70"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.6 }}
        >
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
            Scroll ↓
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-border py-6 bg-background">
        <Marquee speed={38}>
          {["Land", "Homes", "Farms", "Warehouses", "Apartments", "Hotels", "Commercial", "Diaspora", "Titled", "Verified", "Financed", "Managed"].map((w, i) => (
            <span key={i} className="inline-flex items-center gap-6 text-3xl md:text-5xl font-display font-bold text-foreground/90">
              {w}
              <span className="text-accent">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* LIVE LISTINGS TICKER */}
      <ListingsTicker />

      {/* SEARCH BAR */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-14">
        <Reveal>
          <form onSubmit={onSearch} className="bg-card border border-border rounded-3xl p-4 md:p-5 grid md:grid-cols-12 gap-3 items-center shadow-sm" data-testid="hero-search">
            <div className="md:col-span-4 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Mukono, Wakiso, Jinja, Luweero…" value={search.q} onChange={(e) => setSearch({ ...search, q: e.target.value })} className="pl-10 h-12 rounded-full border-border" data-testid="search-q" />
            </div>
            <div className="md:col-span-3">
              <Select value={search.property_type} onValueChange={(v) => setSearch({ ...search, property_type: v })}>
                <SelectTrigger className="h-12 rounded-full border-border" data-testid="search-type"><SelectValue placeholder="Property Type" /></SelectTrigger>
                <SelectContent>{meta.property_types?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Select value={search.title_status} onValueChange={(v) => setSearch({ ...search, title_status: v })}>
                <SelectTrigger className="h-12 rounded-full border-border" data-testid="search-title"><SelectValue placeholder="Title" /></SelectTrigger>
                <SelectContent>{meta.title_statuses?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Select value={search.budget} onValueChange={(v) => setSearch({ ...search, budget: v })}>
                <SelectTrigger className="h-12 rounded-full border-border" data-testid="search-budget"><SelectValue placeholder="Budget" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0-10000000">Under UGX 10M</SelectItem>
                  <SelectItem value="10000000-50000000">10M – 50M</SelectItem>
                  <SelectItem value="50000000-200000000">50M – 200M</SelectItem>
                  <SelectItem value="200000000-99999999999">200M+</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="md:col-span-1 h-12 rounded-full bg-primary hover:bg-primary/90" data-testid="search-submit">Search</Button>
          </form>
        </Reveal>
      </section>

      {/* MANIFESTO — numbered chapters */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="manifesto">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-5">
            <div className="eyebrow mb-3">The Manifesto</div>
            <h2 className="font-display text-4xl md:text-6xl tracking-[-0.03em] leading-[0.95]">Four steps.<br/>One honest process.</h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 self-end">
            <p className="text-muted-foreground text-lg leading-relaxed">Buying land in Uganda has been broken for decades. Fake titles, ghost sellers, boundary disputes. We built TIGAWA to end that.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-x-14 gap-y-10">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.08}>
              <div className="border-t border-border pt-6 group" data-testid={`chapter-${m.n}`}>
                <div className="flex items-baseline gap-5">
                  <div className="font-mono text-sm text-accent">{m.n}</div>
                  <h3 className="font-display text-3xl md:text-4xl tracking-tight">{m.t}</h3>
                </div>
                <p className="mt-4 text-muted-foreground text-base leading-relaxed max-w-md ml-9">{m.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SHOWCASE SLIDES */}
      <SlideShowcase />

      {/* FEATURED LISTINGS */}
      <section className="bg-muted/40" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="flex items-end justify-between gap-6 mb-12 flex-wrap">
            <div>
              <div className="eyebrow mb-3">Featured Opportunities</div>
              <Reveal><h2 className="font-display text-3xl md:text-5xl tracking-[-0.03em] leading-[1.05] max-w-2xl">Hand-picked. Title-checked.<br/>Ready to inspect.</h2></Reveal>
            </div>
            <Button onClick={() => navigate("/listings")} variant="outline" className="rounded-full border-border" data-testid="view-all-btn">
              View all <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p, i) => <PropertyCard p={p} index={i} key={p.id} />)}
          </div>
        </div>
      </section>

      {/* BUY FROM ABROAD */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="diaspora">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-6">
            <div className="relative aspect-[5/6] rounded-3xl overflow-hidden">
              <img src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=1600&q=85" alt="Diaspora buyers" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/70 via-secondary/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="text-[11px] tracking-[0.24em] uppercase font-bold text-accent">Diaspora Package</div>
                <div className="font-display text-3xl md:text-4xl mt-1">Buy from anywhere.</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-6">
            <div className="eyebrow mb-3">Buy from Abroad</div>
            <h2 className="font-display text-4xl md:text-6xl tracking-[-0.03em] leading-[0.95]">From LA to Katosi in <em className="not-italic text-primary">72 hours.</em></h2>
            <p className="mt-6 text-muted-foreground text-lg leading-relaxed max-w-lg">
              Live video inspection. Boundary-check on GPS. Certified title verification. Bank transfer settlement.
              Every step filmed and shared with you before your money moves.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {["Live video site inspection with agent", "Boundary walk with GPS coordinates", "Certified title & lien verification", "Legal transfer via TIGAWA lawyers", "Post-purchase property management"].map((li) => (
                <li key={li} className="flex items-start gap-3"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {li}</li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild className="rounded-full bg-primary hover:bg-primary/90" data-testid="diaspora-wa-btn">
                <a href="https://wa.me/16145719333?text=Hi%20TIGAWA,%20I'm%20a%20diaspora%20buyer" target="_blank" rel="noreferrer">WhatsApp our diaspora desk</a>
              </Button>
              <Button onClick={() => navigate("/agents")} variant="outline" className="rounded-full border-border" data-testid="diaspora-agents-btn">
                Meet our diaspora agents
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-secondary text-secondary-foreground" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <div className="eyebrow text-accent mb-3">Professional Services</div>
              <Reveal><h2 className="font-display text-3xl md:text-5xl tracking-[-0.03em] leading-[1.05] max-w-2xl">The full property stack.</h2></Reveal>
            </div>
            <Button onClick={() => navigate("/services")} className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="services-cta">
              Explore all services <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.05}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 hover:bg-white/[0.08] hover:border-accent/30 transition-all h-full">
                  <s.icon className="w-6 h-6 text-accent" strokeWidth={1.5} />
                  <h3 className="font-display text-xl font-semibold mt-4">{s.t}</h3>
                  <p className="text-sm text-secondary-foreground/75 mt-1.5 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REGIONS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24" data-testid="regions">
        <div className="text-center mb-14">
          <div className="eyebrow mb-3">National Coverage</div>
          <Reveal><h2 className="font-display text-3xl md:text-5xl tracking-[-0.03em]">From Kampala to Karamoja.</h2></Reveal>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {REGIONS.map((r, i) => (
            <Reveal key={r.name} delay={i * 0.05}>
              <div className="group relative bg-card border border-border rounded-2xl p-6 hover-lift overflow-hidden h-full" data-testid={`region-${r.name.toLowerCase()}`}>
                <div className="font-mono text-xs text-muted-foreground">0{i + 1} / 04</div>
                <h3 className="font-display text-2xl font-semibold mt-2 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" /> {r.name}
                </h3>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{r.districts}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="bg-muted/40" data-testid="pricing">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="text-center mb-14">
            <div className="eyebrow mb-3">List Your Property</div>
            <Reveal><h2 className="font-display text-3xl md:text-5xl tracking-[-0.03em]">Choose the listing that fits.</h2></Reveal>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PACKAGES.map((pkg, i) => (
              <Reveal key={pkg.name} delay={i * 0.08}>
                <div className={`bg-card rounded-2xl p-8 border ${pkg.featured ? "border-primary shadow-xl" : "border-border"} relative h-full`} data-testid={`pkg-${pkg.name.toLowerCase()}`}>
                  {pkg.featured && (
                    <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-primary text-primary-foreground text-[10px] tracking-[0.18em] uppercase font-bold">Most Popular</span>
                  )}
                  <div className="eyebrow mb-2">{pkg.name}</div>
                  <div className="font-display text-4xl font-bold">{pkg.price}</div>
                  <ul className="mt-6 space-y-3 text-sm">
                    {pkg.points.map((pt) => (<li key={pt} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {pt}</li>))}
                  </ul>
                  <Button onClick={() => navigate("/sell")} className={`mt-8 w-full rounded-full ${pkg.featured ? "bg-primary hover:bg-primary/90" : "bg-secondary hover:bg-secondary/90"}`} data-testid={`pkg-cta-${pkg.name.toLowerCase()}`}>{pkg.cta}</Button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BANKS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="banks">
        <div className="text-center mb-10">
          <div className="eyebrow mb-3">Banking Partners</div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight">Mortgage & construction financing.</h2>
        </div>
        <div className="flex flex-wrap gap-3 justify-center">
          {BANKS.map((b) => (
            <span key={b} className="px-5 py-3 bg-card border border-border rounded-full text-sm font-semibold hover-lift" data-testid={`bank-${b.toLowerCase().replace(/\s/g, '-')}`}>{b} Bank</span>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <Reveal>
          <div className="bg-secondary text-secondary-foreground rounded-3xl p-10 md:p-16 grid md:grid-cols-12 gap-10 relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/20 blur-3xl" />
            <div className="md:col-span-7 relative z-10">
              <div className="eyebrow text-accent mb-3">Ready?</div>
              <h3 className="font-display text-4xl md:text-5xl tracking-[-0.03em] leading-tight">List your property, or find the one that changes everything.</h3>
            </div>
            <div className="md:col-span-5 flex flex-col justify-end gap-3 relative z-10">
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => navigate("/sell")} className="rounded-full bg-primary hover:bg-primary/90" data-testid="cta-list">List Property</Button>
                <Button onClick={() => navigate("/contact")} variant="outline" className="rounded-full bg-transparent border-white/30 hover:bg-white/10 text-white" data-testid="cta-talk">Talk to us</Button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}

function SlideShowcase() {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 5500);
    return () => clearInterval(timerRef.current);
  }, []);

  const go = (d) => {
    clearInterval(timerRef.current);
    setIdx((i) => (i + d + SLIDES.length) % SLIDES.length);
    timerRef.current = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 5500);
  };

  return (
    <section id="slides" className="bg-secondary text-secondary-foreground" data-testid="showcase">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
          <div>
            <div className="eyebrow text-accent mb-3">Featured Showcase</div>
            <h2 className="font-display text-3xl md:text-5xl tracking-[-0.03em] leading-[1.05]">Four properties.<br/>Four regions.</h2>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => go(-1)} className="w-11 h-11 rounded-full border border-white/20 hover:bg-white/10 grid place-items-center transition-colors" aria-label="Previous" data-testid="slide-prev">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="font-mono text-xs w-16 text-center">{String(idx + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}</div>
            <button onClick={() => go(1)} className="w-11 h-11 rounded-full border border-white/20 hover:bg-white/10 grid place-items-center transition-colors" aria-label="Next" data-testid="slide-next">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="relative aspect-[16/9] rounded-3xl overflow-hidden bg-black">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0"
            >
              <img src={SLIDES[idx].img} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary via-secondary/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-14">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.7 }}
                >
                  <div className="text-[11px] tracking-[0.24em] uppercase font-bold text-accent mb-3">{SLIDES[idx].tag}</div>
                  <h3 className="font-display text-4xl md:text-6xl tracking-[-0.03em] max-w-3xl">{SLIDES[idx].title}</h3>
                  <p className="mt-3 text-secondary-foreground/85 max-w-lg">{SLIDES[idx].caption}</p>
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-6 flex items-center gap-2">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => { clearInterval(timerRef.current); setIdx(i); }}
              className={`h-1 rounded-full transition-all ${i === idx ? "w-12 bg-accent" : "w-6 bg-white/20 hover:bg-white/40"}`}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`slide-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function HeroSlideshow({ slides }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 4500);
    return () => clearInterval(t);
  }, [slides.length]);

  // Preload the next two slides so cross-fades stay smooth
  useEffect(() => {
    const preload = (n) => {
      const src = slides[(idx + n) % slides.length];
      if (!src) return;
      const img = new Image();
      img.src = src;
    };
    preload(1); preload(2);
  }, [idx, slides]);

  return (
    <div className="absolute inset-0" data-testid="hero-slideshow-frames">
      {slides.map((src, i) => {
        const active = i === idx;
        return (
          <motion.div
            key={src}
            className="absolute inset-0"
            initial={false}
            animate={{ opacity: active ? 1 : 0 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            style={{ zIndex: active ? 2 : 1 }}
            data-testid={`hero-slide-${i}`}
          >
            <Link
              to="/listings"
              aria-label={`View plot ${i + 1} listings`}
              className="block absolute inset-0"
              data-testid={`hero-slide-link-${i}`}
              data-cursor="hover"
            >
              <motion.img
                src={src}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ scale: 1.08 }}
                animate={{ scale: active ? 1.16 : 1.08 }}
                transition={{ duration: 6, ease: "linear" }}
                draggable={false}
              />
            </Link>
          </motion.div>
        );
      })}
      <div className="absolute bottom-28 md:bottom-32 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1 rounded-full transition-all ${i === idx ? "w-10 bg-accent" : "w-4 bg-white/30 hover:bg-white/60"}`}
            data-testid={`hero-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

